import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut, ExternalLink, ArrowRight, Radio, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AdminTabs from "@/components/AdminTabs";

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" })
    : "—";

const Chip = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 text-[11px] font-archivo uppercase tracking-widest border ${
      active ? "bg-bb-navy text-white border-bb-navy" : "bg-white text-bb-navy border-border"
    }`}
  >
    {label}
  </button>
);

export default function AdminPressReleasesPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [source, setSource] = useState("all");
  const [promotedFilter, setPromotedFilter] = useState("all"); // all | yes | no
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState({});

  const prSources = useMemo(
    () => sources.filter((s) => s.kind === "press-release"),
    [sources],
  );

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (source !== "all") qs.set("source", source);
    if (promotedFilter !== "all") qs.set("promoted", promotedFilter);
    api
      .get(`/admin/press-releases?${qs.toString()}`, { withCredentials: true })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Failed to load press releases"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api
      .get("/admin/ingest/sources", { withCredentials: true })
      .then((r) => setSources(Array.isArray(r.data) ? r.data : []))
      .catch(() => { /* ignore */ });
  }, []);

  useEffect(() => {
    load();
  }, [source, promotedFilter]);

  const logout = async () => {
    try {
      await api.post("/auth/logout", null, { withCredentials: true });
    } catch { /* ignore */ }
    window.location.href = "/login";
  };

  const promote = async (pr) => {
    setPromoting((p) => ({ ...p, [pr.filing_id]: true }));
    try {
      const r = await api.post(
        `/admin/press-releases/${pr.filing_id}/promote`,
        null,
        { withCredentials: true },
      );
      const draftId = r.data?.draft?.id;
      if (r.data?.already_promoted) {
        toast.info("Already promoted — opening existing draft.");
      } else {
        toast.success("Draft created from press release.");
      }
      if (draftId) nav(`/admin/drafts/${draftId}`);
    } catch {
      toast.error("Promote failed");
    } finally {
      setPromoting((p) => ({ ...p, [pr.filing_id]: false }));
    }
  };

  return (
    <div data-testid="admin-press-releases-page" className="bg-bb-offwhite min-h-screen">
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="bb-container py-3 flex items-center justify-between gap-3">
          <div>
            <div className="bb-overline text-bb-copper">Admin · Press Releases</div>
            <div className="font-archivo font-bold text-bb-navy text-lg leading-tight">
              {items.length} {items.length === 1 ? "release" : "releases"}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-archivo uppercase tracking-widest text-bb-slate hover:text-bb-navy"
          >
            <LogOut size={13} /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
        <AdminTabs />
        <div className="bb-container py-2 flex gap-2 overflow-x-auto">
          <Chip
            label="All sources"
            active={source === "all"}
            onClick={() => setSource("all")}
            testId="pr-source-all"
          />
          {prSources.map((s) => (
            <Chip
              key={s.source_id}
              label={s.label}
              active={source === s.source_id}
              onClick={() => setSource(s.source_id)}
              testId={`pr-source-${s.source_id}`}
            />
          ))}
          <span className="w-px h-6 bg-border mx-1 self-center" aria-hidden />
          {[["all", "All"], ["no", "Not promoted"], ["yes", "Promoted"]].map(([v, l]) => (
            <Chip
              key={v}
              label={l}
              active={promotedFilter === v}
              onClick={() => setPromotedFilter(v)}
              testId={`pr-promoted-${v}`}
            />
          ))}
        </div>
      </header>

      <div className="bb-container py-4 space-y-3">
        {loading ? (
          <div className="text-center py-10 bb-overline text-bb-slate">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-border p-8 text-center">
            <Radio size={20} className="text-bb-copper mx-auto" />
            <div className="bb-overline text-bb-copper mt-3">No press releases match</div>
            <p className="text-sm text-bb-slate mt-2">
              Adjust the source or promotion filter above, or wait for the next scheduled ingest (every 3 hours).
            </p>
          </div>
        ) : (
          items.map((pr) => {
            const isBusy = !!promoting[pr.filing_id];
            const isPromoted = !!pr.promoted_draft_id;
            const tags = pr.tags || {};
            const oneLine = tags.one_line_summary;
            const sourceLabel =
              prSources.find((s) => s.source_id === pr.source)?.label || pr.source;
            return (
              <article
                key={pr.filing_id}
                className="bg-white border border-border p-4 flex flex-col gap-3"
                data-testid={`pr-${pr.filing_id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-[10px] font-mono px-2 py-0.5 bg-bb-navy text-white"
                    data-testid={`pr-source-${pr.filing_id}`}
                  >
                    {(sourceLabel || "").toUpperCase()}
                  </span>
                  {isPromoted && (
                    <span
                      className="text-[10px] font-mono px-2 py-0.5 bb-tag-positive inline-flex items-center gap-1"
                      data-testid={`pr-${pr.filing_id}-promoted-badge`}
                    >
                      <CheckCircle2 size={11} /> PROMOTED
                    </span>
                  )}
                  {tags.confidence != null && (
                    <span className="text-[10px] font-mono text-bb-slate">
                      conf {Number(tags.confidence).toFixed(2)}
                    </span>
                  )}
                  <span className="text-[11px] font-mono text-bb-slate ml-auto">
                    {fmt(pr.effective_date || pr.ingested_at)}
                  </span>
                </div>
                <h3 className="font-archivo text-base sm:text-lg text-bb-navy font-semibold leading-snug">
                  {pr.title}
                </h3>
                {oneLine ? (
                  <p className="text-sm text-bb-navy leading-relaxed italic border-l-2 border-bb-copper pl-3">
                    “{oneLine}”
                  </p>
                ) : null}
                {pr.summary ? (
                  <p className="text-sm text-bb-slate leading-relaxed line-clamp-4">
                    {pr.summary}
                  </p>
                ) : null}
                <div className="text-[11px] font-mono text-bb-slate flex flex-wrap gap-x-3 gap-y-1">
                  {tags.commodity_id && <span>commodity: {tags.commodity_id}</span>}
                  {tags.company_id && <span>company: {tags.company_id}</span>}
                  {tags.region_id && <span>region: {tags.region_id}</span>}
                  {tags.project_id && <span>project: {tags.project_id}</span>}
                </div>
                <div className="flex items-center justify-between gap-2 pt-3 border-t border-border flex-wrap">
                  <a
                    href={pr.registry_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-mono text-bb-navy bb-link"
                    data-testid={`pr-${pr.filing_id}-source-link`}
                  >
                    <ExternalLink size={11} /> Read release
                  </a>
                  <button
                    onClick={() => promote(pr)}
                    disabled={isBusy}
                    data-testid={`pr-${pr.filing_id}-promote`}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-archivo uppercase tracking-widest min-h-[44px] disabled:opacity-50 ${
                      isPromoted
                        ? "bg-white text-bb-navy border border-bb-copper"
                        : "bb-btn-primary"
                    }`}
                  >
                    {isBusy ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <ArrowRight size={14} />
                    )}
                    {isPromoted ? "Open draft" : "Promote → Draft"}
                  </button>
                </div>
              </article>
            );
          })
        )}
      </div>

      <div className="bb-container py-6 text-center">
        <Link
          to="/"
          className="text-xs font-archivo uppercase tracking-widest text-bb-slate bb-link"
        >
          Back to site
        </Link>
      </div>
    </div>
  );
}
